import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const loadPayPalSdk = (clientId, mode) =>
  new Promise((resolve, reject) => {
    if (window.paypal) { resolve(window.paypal); return; }
    const script = document.createElement("script");
    const envParam = mode === "sandbox" ? "&debug=false" : "";
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=EUR&intent=capture${envParam}`;
    script.async = true;
    script.onload = () => resolve(window.paypal);
    script.onerror = () => reject(new Error("Impossibile caricare PayPal SDK"));
    document.body.appendChild(script);
  });

export default function PayPalCheckout() {
  const { txId } = useParams();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const renderedRef = useRef(false);

  useEffect(() => {
    api.get(`/payments/${txId}/receipt`)
      .then((r) => {
        const t = r.data.transaction;
        setTx(t);
        setLoading(false);
      })
      .catch((e) => { toast.error(formatApiError(e)); navigate("/area-riservata"); });
  }, [txId, navigate]);

  useEffect(() => {
    if (!tx || renderedRef.current) return;
    renderedRef.current = true;
    (async () => {
      try {
        const settingsRes = await api.get("/agency/payment-settings");
        const s = settingsRes.data;
        if (!s.paypal_client_id) {
          toast.error("PayPal non configurato dall'agenzia");
          return;
        }
        const paypal = await loadPayPalSdk(s.paypal_client_id, s.paypal_mode || "sandbox");
        paypal.Buttons({
          createOrder: (_data, actions) =>
            actions.order.create({
              purchase_units: [{
                reference_id: tx.reference,
                description: `Pagamento polizza ${tx.metadata?.policy_number || ""}`,
                amount: { value: tx.amount.toFixed(2), currency_code: "EUR" },
              }],
            }),
          onApprove: async (data) => {
            try {
              const res = await api.post(`/payments/paypal/capture/${tx.id}`, { order_id: data.orderID });
              if (res.data.payment_status === "paid") {
                navigate(`/area-riservata/ricevuta/${tx.id}`);
              } else {
                toast.error("Pagamento PayPal non confermato");
              }
            } catch (e) { toast.error(formatApiError(e)); }
          },
          onError: (err) => {
            toast.error("Errore PayPal: " + (err?.message || "sconosciuto"));
          },
          onCancel: () => {
            toast.info("Pagamento annullato");
            navigate(`/area-riservata/pagamento/${tx.policy_id}`);
          },
        }).render(containerRef.current);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [tx, navigate]);

  if (loading || !tx) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="paypal-checkout">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <button onClick={() => navigate(`/area-riservata/pagamento/${tx.policy_id}`)} className="text-xs text-slate-300 inline-flex items-center gap-1 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Cambia metodo
          </button>
        </div>
      </header>
      <main className="max-w-md mx-auto px-6 py-10">
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
          <h1 className="font-display text-2xl font-semibold mb-2">Pagamento PayPal</h1>
          <p className="text-sm text-slate-500 mb-1">Polizza n° {tx.metadata?.policy_number}</p>
          <p className="text-3xl font-display font-semibold tabular-nums my-4">{formatCurrency(tx.amount)}</p>
          <p className="text-xs text-slate-400 mb-6">Causale: <span className="font-mono">{tx.reference}</span></p>
          <div ref={containerRef} data-testid="paypal-buttons-container" />
        </div>
      </main>
    </div>
  );
}
